/**
 * AI 计划（Plan）Pinia store。
 *
 * <p>后端把 master agent 的任务清单从"瞬时 tasks[]"升级为持久化 Plan 实体之后，
 * 每个 plan_snapshot SSE 事件都携带着 plan 完整状态（status / version / tasks / signature）。
 * 本 store 负责：</p>
 *
 * <ul>
 *   <li>按 sessionId 维度缓存最新 plan snapshot（同一会话重复收到则按 version 丢弃旧的）</li>
 *   <li>提供 UI 用的 computed: 活跃任务、按 status 分桶、是否已完成</li>
 *   <li>在用户切会话或删除会话时清理状态；同一会话的后续发送不主动清空，避免「继续」后清单空白</li>
 * </ul>
 *
 * <p>设计原则：</p>
 * <ul>
 *   <li>Plan 是后端的真相，前端只读不写</li>
 *   <li>纯 reactive ref + computed，不依赖任何业务 store，方便单测</li>
 *   <li>字段裁剪到 UI 真正会用到的（taskDescription / status / lastSummary），
 *       不引入业务语义判断（例如 "完成" → 不在此处做，按 status 字符串原样透出）</li>
 * </ul>
 */
import { defineStore } from 'pinia'
import { registerTenantScopedReset } from '@/stores/_tenantScopedRegistry'
import { computed, ref } from 'vue'
export type AiPlanTaskStatus =
  | 'PENDING'
  | 'READY'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | 'SKIPPED'

export interface AiPlanSnapshotTask {
  taskId: string
  taskDescription: string
  status: AiPlanTaskStatus
  priority: number
  lastSummary: string | null
  errorMessage: string | null
}

export interface AiPlanSnapshotEvent {
  sessionId?: string
  goal?: string
  steps?: Array<{
    id?: string
    title?: string
    status?: string
    note?: string | null
  }>
}

export interface AiPlanSnapshotView {
  planGoal: string | null
  status: string
  tasks: AiPlanSnapshotTask[]
  updatedAt: string
}

export interface AiPlanState {
  sessionId: string
  snapshot: AiPlanSnapshotView
}

const EMPTY_SNAPSHOT: AiPlanSnapshotView = {
  planGoal: null,
  status: 'IN_PROGRESS',
  tasks: [],
  updatedAt: ''
}

function normalizePlanTaskStatus(status: string | undefined): AiPlanTaskStatus {
  if (status === 'RUNNING') {
    return 'IN_PROGRESS'
  }
  if (
    status === 'PENDING'
    || status === 'READY'
    || status === 'IN_PROGRESS'
    || status === 'COMPLETED'
    || status === 'FAILED'
    || status === 'SKIPPED'
  ) {
    return status
  }
  return 'PENDING'
}

export const useAiPlanStore = defineStore('aiPlan', () => {
  /** 按 sessionId 缓存的 plan state。 */
  const plans = ref<Map<string, AiPlanState>>(new Map())

  const hasAnyPlan = computed(() => plans.value.size > 0)

  function getState(sessionId: string | null | undefined): AiPlanState | null {
    if (!sessionId) {
      return null
    }
    return plans.value.get(sessionId) ?? null
  }

  function getSnapshot(sessionId: string | null | undefined): AiPlanSnapshotView {
    return getState(sessionId)?.snapshot ?? EMPTY_SNAPSHOT
  }

  function isTerminalStatus(status: AiPlanTaskStatus | string): boolean {
    return status === 'COMPLETED' || status === 'FAILED' || status === 'SKIPPED'
  }

  /**
   * 应用一个 plan_snapshot 事件。按 sessionId 维度去重 + 按 version 升级。
   * 如果后端发来一个比当前更旧 version 的 snapshot（网络重排场景），直接丢弃。
   */
  function applySnapshot(sessionId: string, snapshot: AiPlanSnapshotView): void {
    if (!sessionId) {
      return
    }
    const next = new Map(plans.value)
    next.set(sessionId, { sessionId, snapshot })
    plans.value = next
  }

  /** 切会话 / 删会话时清空（避免跨会话残留）。同一会话后续发送不要调用。 */
  function resetForSession(sessionId: string): void {
    const next = new Map(plans.value)
    next.delete(sessionId)
    plans.value = next
  }

  /** 清空全部 plan 缓存（登出 / 切换租户时调用，防止跨租户计划残留）。 */
  function resetAll(): void {
    plans.value = new Map()
  }

  function fromSsePayload(event: AiPlanSnapshotEvent): AiPlanSnapshotView {
    const steps = Array.isArray(event.steps) ? event.steps : []
    const tasks = steps.map((step, index): AiPlanSnapshotTask => ({
      taskId: step.id || `step-${index + 1}`,
      taskDescription: step.title || `步骤 ${index + 1}`,
      status: normalizePlanTaskStatus(step.status),
      priority: index + 1,
      lastSummary: step.note ?? null,
      errorMessage: null
    }))
    return {
      planGoal: event.goal ?? null,
      status: tasks.every((task) => isTerminalStatus(task.status)) ? 'COMPLETED' : 'IN_PROGRESS',
      tasks,
      updatedAt: new Date().toISOString()
    }
  }

  function applySsePayload(event: AiPlanSnapshotEvent): void {
    if (!event.sessionId) {
      return
    }
    applySnapshot(event.sessionId, fromSsePayload(event))
  }

  const activeTasksBySession = computed(() => (sessionId: string | null | undefined) => {
    const state = getState(sessionId)
    if (!state) {
      return [] as AiPlanSnapshotTask[]
    }
    return state.snapshot.tasks.filter((t) => !isTerminalStatus(t.status))
  })

  const completedTasksBySession = computed(() => (sessionId: string | null | undefined) => {
    const state = getState(sessionId)
    if (!state) {
      return [] as AiPlanSnapshotTask[]
    }
    return state.snapshot.tasks.filter((t) => t.status === 'COMPLETED')
  })

  return {
    plans,
    hasAnyPlan,
    getState,
    getSnapshot,
    applySnapshot,
    applySsePayload,
    fromSsePayload,
    resetForSession,
    resetAll,
    activeTasksBySession,
    completedTasksBySession
  }
})

registerTenantScopedReset('aiPlan', () => useAiPlanStore().resetAll())

export type AiPlanStore = ReturnType<typeof useAiPlanStore>
